import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DIVISIONS, LANGUAGES } from '@/config/appConfig';
import type { AdminUser, CountryOption, Division, UpdateUserRequest, UserRole } from '../types/admin.types';

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().every((v, i) => [...b].sort()[i] === v);

interface Props {
  user: AdminUser | null;  // null = closed
  isLoading: boolean;
  isSuperAdmin: boolean;
  countries: CountryOption[];
  onSubmit: (userId: number, data: UpdateUserRequest, newRol?: UserRole) => Promise<void>;
  onClose: () => void;
}

export const EditUserModal = ({ user, isLoading, isSuperAdmin, countries, onSubmit, onClose }: Props) => {
  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState<number | ''>('');
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [language, setLanguage] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [userFunction, setUserFunction] = useState('');
  const [scope, setScope] = useState('');
  const [seniority, setSeniority] = useState('');
  const [rol, setRol] = useState<UserRole | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Sync form state when the target user changes (modal opens for a different user)
  useEffect(() => {
    if (!user) return;
    setName(user.name ?? '');
    setCountryId(user.country_id ?? '');
    setDivisions(user.user_meta.division ?? []);
    setLanguage(user.user_meta.language ?? '');
    setJobTitle(user.user_meta.job_title ?? '');
    setUserFunction(user.user_meta.function ?? '');
    setScope(user.user_meta.scope ?? '');
    setSeniority(user.user_meta.seniority ?? '');
    setRol(user.user_meta.rol);
    setError(null);
  }, [user]);

  if (!user) return null;

  const originalRol = user.user_meta.rol;
  const canEditRole = isSuperAdmin && originalRol !== 'super_admin';

  const toggleDivision = (d: Division) =>
    setDivisions((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);

  const hasChanges =
    name.trim() !== (user.name ?? '') ||
    countryId !== (user.country_id ?? '') ||
    !arraysEqual(divisions, user.user_meta.division ?? []) ||
    language !== (user.user_meta.language ?? '') ||
    jobTitle.trim() !== (user.user_meta.job_title ?? '') ||
    userFunction.trim() !== (user.user_meta.function ?? '') ||
    scope.trim() !== (user.user_meta.scope ?? '') ||
    seniority.trim() !== (user.user_meta.seniority ?? '') ||
    (canEditRole && rol !== originalRol);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    setError(null);
    try {
      const profileData: UpdateUserRequest = {};
      if (name.trim() !== (user.name ?? '')) profileData.name = name.trim();
      if (countryId !== (user.country_id ?? '')) profileData.country_id = countryId === '' ? undefined : countryId;
      if (!arraysEqual(divisions, user.user_meta.division ?? [])) profileData.division = divisions;
      if (language !== (user.user_meta.language ?? '')) profileData.language = language || undefined;
      if (jobTitle.trim() !== (user.user_meta.job_title ?? '')) profileData.job_title = jobTitle.trim();
      if (userFunction.trim() !== (user.user_meta.function ?? '')) profileData.function = userFunction.trim() || undefined;
      if (scope.trim() !== (user.user_meta.scope ?? '')) profileData.scope = scope.trim() || undefined;
      if (seniority.trim() !== (user.user_meta.seniority ?? '')) profileData.seniority = seniority.trim() || undefined;
      const rolChanged = canEditRole && rol !== originalRol && rol !== '';
      await onSubmit(user.user_id, profileData, rolChanged ? (rol as UserRole) : undefined);
      onClose();
    } catch {
      setError('Failed to save changes. Please try again.');
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        {/* No inner max-h/overflow here — DialogContent's own scroll region handles
            long forms like this one (8 fields), avoiding a nested scroll container. */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Name */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-name" className="text-sm font-medium text-slate-700">
              Name
            </label>
            <input
              id="edit-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Country */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-country" className="text-sm font-medium text-slate-700">
              Country
            </label>
            <select
              id="edit-country"
              value={countryId}
              onChange={(e) => setCountryId(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400 bg-white"
            >
              <option value="">Select a country…</option>
              {countries.map((c) => (
                <option key={c.country_id} value={c.country_id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Division — multi-select checkboxes */}
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-slate-700">Division</span>
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 flex flex-wrap gap-x-4 gap-y-2">
              {DIVISIONS.map((d) => (
                <label key={d} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={divisions.includes(d as Division)}
                    onChange={() => toggleDivision(d as Division)}
                    disabled={isLoading}
                    className="rounded"
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>

          {/* Language */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-language" className="text-sm font-medium text-slate-700">
              Language
            </label>
            <select
              id="edit-language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400 bg-white"
            >
              <option value="">Select a language…</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          {/* Job Title */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-job-title" className="text-sm font-medium text-slate-700">
              Job Title
            </label>
            <input
              id="edit-job-title"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="e.g. Technical Sales Manager"
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Function */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-function" className="text-sm font-medium text-slate-700">
              Function
            </label>
            <input
              id="edit-function"
              type="text"
              value={userFunction}
              onChange={(e) => setUserFunction(e.target.value)}
              placeholder="e.g. Sales"
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Scope */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-scope" className="text-sm font-medium text-slate-700">
              Scope
            </label>
            <input
              id="edit-scope"
              type="text"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              placeholder="e.g. Regional"
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Seniority */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="edit-seniority" className="text-sm font-medium text-slate-700">
              Seniority
            </label>
            <input
              id="edit-seniority"
              type="text"
              value={seniority}
              onChange={(e) => setSeniority(e.target.value)}
              placeholder="e.g. Senior"
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                         focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                         disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>

          {/* Role — super_admin only, cannot change super_admin rows */}
          {canEditRole && (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="edit-role" className="text-sm font-medium text-slate-700">
                Role
              </label>
              <select
                id="edit-role"
                value={rol}
                onChange={(e) => setRol(e.target.value as UserRole)}
                disabled={isLoading}
                className="rounded-lg border border-slate-200 px-3 py-2 text-base outline-none
                           focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition
                           disabled:bg-slate-50 disabled:text-slate-400 bg-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !hasChanges}>
              {isLoading ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
